import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { Button, Card, ProgressBar, Row, Col } from 'react-bootstrap';
import HealthCheck from './HealthCheck';
import DiseasePredictionSystem from '../context/SymptomsData';

const EnterpriseSymptomChecker = ({ onFindDoctor }) => {
    // INTERNAL STATE
    const [step, setStep] = useState(1);
    const [selectedBodyPart, setSelectedBodyPart] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [diagnosisResult, setDiagnosisResult] = useState(null);

    // DATA
    const symptomData = {
        'Head': [
            { name: 'Severe Headache', spec: 'General Physician', condition: 'Migraine or Tension Headache', urgency: 'Moderate' },
            { name: 'Dizziness / Vertigo', spec: 'Neurologist', condition: 'Inner Ear Issue or Vertigo', urgency: 'High' },
            { name: 'Blurred Vision', spec: 'Ophthalmologist', condition: 'Eye Strain or Vision Problem', urgency: 'Low' }
        ],
        'Chest': [
            { name: 'Chest Pain', spec: 'Cardiologist', condition: 'Angina or Heart Related', urgency: 'Critical' },
            { name: 'Shortness of Breath', spec: 'Pulmonologist', condition: 'Asthma or Respiratory Infection', urgency: 'High' },
            { name: 'Palpitations', spec: 'Cardiologist', condition: 'Arrhythmia or Anxiety', urgency: 'Moderate' }
        ],
        'Stomach': [
            { name: 'Stomach Ache', spec: 'Gastroenterologist', condition: 'Gastritis or Indigestion', urgency: 'Low' },
            { name: 'Nausea / Vomiting', spec: 'General Physician', condition: 'Food Poisoning or Viral', urgency: 'Moderate' },
            { name: 'Acid Reflux', spec: 'Gastroenterologist', condition: 'GERD', urgency: 'Low' }
        ],
        'Joints': [
            { name: 'Knee Pain', spec: 'Orthopedist', condition: 'Arthritis or Injury', urgency: 'Moderate' },
            { name: 'Back Pain', spec: 'Orthopedist', condition: 'Muscle Strain or Disc Issue', urgency: 'Moderate' },
            { name: 'Swelling', spec: 'Orthopedist', condition: 'Inflammation or Injury', urgency: 'Low' }
        ],
        'Skin': [
            { name: 'Rashes / Itching', spec: 'Dermatologist', condition: 'Allergy or Eczema', urgency: 'Low' },
            { name: 'Acne / Pimples', spec: 'Dermatologist', condition: 'Hormonal Acne', urgency: 'Low' },
            { name: 'Hair Loss', spec: 'Dermatologist', condition: 'Alopecia or Deficiency', urgency: 'Low' }
        ]
    };

    const bodyPartUiMap = {
        'Head': { label: 'Head & Neck', sub: 'Migraines, Sinus, Throat', icon: 'solar:user-bold' },
        'Chest': { label: 'Chest & Lungs', sub: 'Heart, Breathing, Ribs', icon: 'solar:heart-pulse-bold' },
        'Stomach': { label: 'Abdomen & Gut', sub: 'Digestive, Liver, Pain', icon: 'mdi:stomach' },
        'Joints': { label: 'Limbs & Joints', sub: 'Arms, Legs, Mobility', icon: 'solar:bone-bold' },
        'Skin': { label: 'Skin & Surface', sub: 'Rashes, Irritation, Scars', icon: 'solar:bacteria-bold' },
    };

    // HANDLERS
    const handleBodyPartSelect = (part) => {
        setSelectedBodyPart(part);
        setStep(2);
    };

    const handleSymptomSelect = (symptom) => {
        setStep(3);
        setIsAnalyzing(true);
        setTimeout(() => {
            setDiagnosisResult(symptom);
            setIsAnalyzing(false);
            setStep(4);
        }, 1500);
    };

    const handleReset = () => {
        setStep(1);
        setSelectedBodyPart(null);
        setDiagnosisResult(null);
        setIsAnalyzing(false);
    };

    const handleFinalAction = () => {
        if (onFindDoctor && diagnosisResult) {
            onFindDoctor(diagnosisResult.spec);
            handleReset();
        }
    };

    const getProgress = () => {
        if (step === 1) return 25;
        if (step === 2) return 50;
        if (step === 3) return 75;
        return 100;
    };
     const[hc,setHc]=useState(false);
    const predictHealth=()=>{
        console.log("abhi")
        setHc(true);
        
    }

    // DYNAMIC ICON MAP FOR SPECIALISTS
    const getSpecialistIcon = (spec) => {
        const map = {
            'Cardiologist': 'solar:heart-pulse-bold',
            'Neurologist': 'solar:brain-bold',
            'Dermatologist': 'solar:bacteria-bold',
            'Orthopedist': 'solar:bone-bold',
            'Gastroenterologist': 'mdi:stomach',
            'Pulmonologist': 'icon-park-solid:lungs',
            'Ophthalmologist': 'solar:eye-bold',
            'General Physician': 'solar:stethoscope-bold'
        };
        // Fallback to stethoscope per user request
        return map[spec] || 'solar:stethoscope-bold';
    };

    // CUSTOM STYLES
    const primaryColor = '#00696b'; // Tea Green / Teal

    return (
        <div className="d-flex flex-column h-100 bg-white font-sans text-dark">
            {/* COMPONENT CONTAINER */}
            <div className="d-flex flex-column flex-grow-1 overflow-auto">

                {/* HEADER */}
                <div className="sticky-top bg-white border-bottom px-4 py-3 d-flex align-items-center justify-content-between z-1030">
                    <div className="d-flex align-items-center gap-2 text-muted small">
                        <span>Clinical Portal</span>
                        <Icon icon="solar:alt-arrow-right-linear" />
                        <span className="fw-medium text-dark">Symptom Assessment</span>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center gap-2 rounded-pill border bg-light px-3 py-1">
                            <span className="spinner-grow spinner-grow-sm text-success" role="status" aria-hidden="true"></span>
                            <span className="small fw-bold text-dark">SYSTEMS ACTIVE</span>
                        </div>
                    </div>
                </div>

                <div className="flex-grow-1 p-4">
                    <Row className="g-4 h-100">
                        {/* LEFT COLUMN: VISUAL WIZARD */}
                        <Col lg={12} xl={8}>
                            <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden">

                                {/* HEADER & PROGRESS */}
                                <Card.Header className="bg-white border-bottom px-4 py-4">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <div>
                                            <h2 className="h4 fw-bold mb-1 text-dark">
                                                {step === 4 ? 'Analysis Complete' : 'Initial Assessment'}
                                            </h2>
                                            <p className="text-muted small mb-0">
                                                {step === 1 && "Step 1: Identify the area of discomfort"}
                                                {step === 2 && `Step 2: Specify symptoms for ${selectedBodyPart}`}
                                                {step === 3 && "Step 3: Processing data..."}
                                                {step === 4 && "Step 4: Review findings & recommendations"}
                                            </p>
                                        </div>
                                        <div className="text-end" style={{ minWidth: '150px' }}>
                                            <span className="h5 fw-bold" style={{ color: primaryColor }}>{getProgress()}%</span>
                                            <ProgressBar now={getProgress()} variant="success" className="mt-1" style={{ height: '6px' }} />
                                        </div>
                                    </div>

                                    {/* BREADCRUMBS */}
                                    <div className="d-flex align-items-center gap-3">
                                        {['Locate', 'Details', 'Analysis', 'Review'].map((label, index) => {
                                            const stepNum = index + 1;
                                            const isActive = step >= stepNum;
                                            return (
                                                <React.Fragment key={label}>
                                                    <div className={`d-flex align-items-center gap-2 ${isActive ? 'text-dark' : 'text-muted'}`}>
                                                        <div className={`d-flex align-items-center justify-content-center rounded-circle small fw-bold ${isActive ? 'text-white' : 'border'}`}
                                                            style={{ width: '24px', height: '24px', backgroundColor: isActive ? primaryColor : 'transparent' }}>
                                                            {stepNum}
                                                        </div>
                                                        <span className="small fw-bold">{label}</span>
                                                    </div>
                                                    {stepNum < 4 && <div className="flex-grow-1 bg-light" style={{ height: '1px', minWidth: '20px' }}></div>}
                                                </React.Fragment>
                                            )
                                        })}
                                    </div>
                                </Card.Header>

                                {/* BODY CONTENT */}
                                <Card.Body className="p-4">
                                    <Row className="h-100">
                                        {/* VISUALIZATION */}
                                        <Col lg={5} className="d-flex align-items-center justify-content-center bg-light rounded-4 position-relative p-4 mb-4 mb-lg-0">
                                            <Button variant="white" className="position-absolute top-0 start-0 m-3 border shadow-sm p-2 d-flex" onClick={handleReset}>
                                                <Icon icon="solar:restart-bold" />
                                            </Button>

                                            <div className="opacity-75 position-relative">
                                                <svg className="text-secondary" height="400" viewBox="0 0 100 200" width="200" style={{ transition: 'all 0.3s ease' }}>
                                                    <circle cx="50" cy="15" r="10"
                                                        fill={selectedBodyPart === 'Head' || selectedBodyPart === 'Skin' ? primaryColor : '#cbd5e1'}
                                                        style={{ transition: 'fill 0.3s ease' }} />
                                                    <path d="M35 30 H65 L70 80 H30 Z"
                                                        fill={selectedBodyPart === 'Chest' || selectedBodyPart === 'Stomach' || selectedBodyPart === 'Skin' ? primaryColor : '#cbd5e1'}
                                                        style={{ transition: 'fill 0.3s ease' }} />
                                                    <rect x="20" y="32" width="10" height="50" rx="5"
                                                        fill={selectedBodyPart === 'Joints' || selectedBodyPart === 'Skin' ? primaryColor : '#cbd5e1'}
                                                        style={{ transition: 'fill 0.3s ease' }} />
                                                    <rect x="70" y="32" width="10" height="50" rx="5"
                                                        fill={selectedBodyPart === 'Joints' || selectedBodyPart === 'Skin' ? primaryColor : '#cbd5e1'}
                                                        style={{ transition: 'fill 0.3s ease' }} />
                                                    <rect x="35" y="85" width="12" height="70" rx="6"
                                                        fill={selectedBodyPart === 'Joints' || selectedBodyPart === 'Skin' ? primaryColor : '#cbd5e1'}
                                                        style={{ transition: 'fill 0.3s ease' }} />
                                                    <rect x="53" y="85" width="12" height="70" rx="6"
                                                        fill={selectedBodyPart === 'Joints' || selectedBodyPart === 'Skin' ? primaryColor : '#cbd5e1'}
                                                        style={{ transition: 'fill 0.3s ease' }} />
                                                </svg>
                                                {step > 1 && selectedBodyPart && (
                                                    <div className="position-absolute bottom-0 start-50 translate-middle-x bg-white px-3 py-1 rounded-pill shadow-sm border mb-4 text-nowrap">
                                                        <small className="fw-bold text-uppercase" style={{ color: primaryColor }}>{selectedBodyPart} Selected</small>
                                                    </div>
                                                )}
                                            </div>
                                            {/* DYNAMIC LABEL LOGIC */}
                                            {!selectedBodyPart && (
                                                <small className="position-absolute bottom-0 mb-3 text-muted fw-bold text-uppercase small" style={{ fontSize: '0.65rem', letterSpacing: '1px' }}>Frontal Perspective</small>
                                            )}
                                        </Col>

                                        {/* INTERACTION PANEL */}
                                        <Col lg={7} className="d-flex flex-column justify-content-center ps-lg-5">

                                            {/* STEP 1: BODY SELECTION */}
                                            {step === 1 && (
                                                <div className="fade-in">
                                                    <small className="text-muted fw-bold text-uppercase d-block mb-3" style={{ letterSpacing: '1px' }}>Select a Region</small>
                                                    <Row className="g-3">
                                                        {Object.keys(symptomData).map((part) => (
                                                            <Col xs={6} key={part}>
                                                                <button
                                                                    onClick={() => handleBodyPartSelect(part)}
                                                                    className="w-100 p-3 text-start border rounded-4 bg-white d-flex align-items-center gap-3 transition-all hover-shadow"
                                                                    style={{ transition: 'all 0.2s', borderColor: '#e2e8f0' }}
                                                                    onMouseOver={(e) => { e.currentTarget.style.borderColor = primaryColor; e.currentTarget.style.backgroundColor = '#fdfefe' }}
                                                                    onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.backgroundColor = 'white' }}
                                                                >
                                                                    <div className="d-flex align-items-center justify-content-center rounded-3 text-dark bg-light" style={{ width: '40px', height: '40px' }}>
                                                                        <Icon icon={bodyPartUiMap[part]?.icon || 'solar:health-bold'} width="24" className="text-secondary" />
                                                                    </div>
                                                                    <div>
                                                                        <div className="fw-bold text-dark small">{bodyPartUiMap[part]?.label || part}</div>
                                                                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>{bodyPartUiMap[part]?.sub}</div>
                                                                    </div>
                                                                </button>
                                                            </Col>
                                                        ))}
                                                    </Row>
                                                </div>
                                            )}

                                            {/* STEP 2: SYMPTOM SELECTION */}
                                            {step === 2 && selectedBodyPart && (
                                                <div className="fade-in">
                                                    <button onClick={handleReset} className="btn btn-link text-decoration-none text-muted p-0 mb-3 small d-flex align-items-center gap-1">
                                                        <Icon icon="solar:arrow-left-linear" /> Back to Regions
                                                    </button>
                                                    <h3 className="h5 fw-bold text-dark mb-4">What specific symptom are you feeling?</h3>
                                                    <div className="d-flex flex-column gap-3">
                                                        {symptomData[selectedBodyPart].map((sym, idx) => (
                                                            <button
                                                                key={idx}
                                                                onClick={() => handleSymptomSelect(sym)}
                                                                className="w-100 p-3 text-start border rounded-4 bg-white d-flex justify-content-between align-items-center transition-all"
                                                                onMouseOver={(e) => { e.currentTarget.classList.add('shadow-sm'); e.currentTarget.style.borderColor = primaryColor }}
                                                                onMouseOut={(e) => { e.currentTarget.classList.remove('shadow-sm'); e.currentTarget.style.borderColor = '#e2e8f0' }}
                                                            >
                                                                <span className="fw-semibold text-dark">{sym.name}</span>
                                                                <Icon icon="solar:alt-arrow-right-linear" className="text-muted" />
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* STEP 3: LOADING */}
                                            {step === 3 && isAnalyzing && (
                                                <div className="text-center py-5 fade-in">
                                                    <div className="spinner-border mb-3" style={{ width: '3rem', height: '3rem', color: primaryColor }} role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <h5 className="fw-bold text-dark">Analyzing Symptoms...</h5>
                                                    <p className="text-muted small">Cross-referencing with enterprise database</p>
                                                </div>
                                            )}

                                            {/* STEP 4: RESULT */}
                                            {step === 4 && diagnosisResult && (
                                                <div className="fade-in">
                                                    {/* Fixed invalid p-9 mb-10 (Bootstrap stops at 5). Used style for larger spacing if needed. */}
                                                    <div className="text-white rounded-4 p-5 mb-5 position-relative overflow-hidden"
                                                        style={{ backgroundColor: primaryColor, padding: '3.5rem !important' }}> {/* Added custom padding to match 'p-9' intent */}
                                                        <Icon icon="solar:medical-kit-bold" className="position-absolute top-0 end-0 text-white opacity-25" style={{ fontSize: '8rem', margin: '-1rem -1rem 0 0' }} />
                                                        <span className="badge bg-white bg-opacity-25 text-white mb-3 fw-bold text-uppercase small">Analysis Result</span>
                                                        <h3 className="fw-bold mb-1 text-white">{diagnosisResult.condition}</h3>
                                                        <p className="text-white-50 small mb-4">Based on reported {selectedBodyPart} symptoms</p>

                                                        <div className="d-flex align-items-center gap-3 border-top border-white border-opacity-25 pt-3 mt-3">
                                                            <div className="bg-white rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{ width: '56px', height: '56px', color: primaryColor }}>
                                                                <Icon icon={getSpecialistIcon(diagnosisResult.spec)} width="32" />
                                                            </div>
                                                            <div>
                                                                <small className="text-white-50 fw-bold text-uppercase d-block" style={{ fontSize: '0.7rem' }}>Recommended Specialist</small>
                                                                <div className="fw-bold fs-5 text-white">{diagnosisResult.spec}</div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="d-flex gap-3 mb-2">
                                                        <Button variant="outline-secondary" className="flex-grow-1 border fw-bold rounded-3 py-2" onClick={handleReset}>
                                                            Check Again
                                                        </Button>
                                                        <Button className="flex-grow-1 text-white fw-bold rounded-3 py-2 d-flex align-items-center justify-content-center gap-2 shadow-sm"
                                                            style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
                                                            onClick={handleFinalAction}>
                                                            Find {diagnosisResult.spec}s <Icon icon="solar:arrow-right-linear" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}

                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* RIGHT COLUMN: INFO PANELS */}
                        <Col xl={4} className="d-none d-xl-block">
                            <div className="d-flex flex-column gap-4">
                                {/* HISTORY */}
                                <Card className="border shadow-sm rounded-4">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <small className="fw-bold text-uppercase text-dark" style={{ letterSpacing: '1px' }}>Recent Checks</small>
                                            <Icon icon="solar:history-bold" className="text-muted" />
                                        </div>
                                        <div className="d-flex flex-column gap-3">
                                            <div className="p-3 bg-light rounded-3 border-start border-4 border-success">
                                                <div className="d-flex justify-content-between align-items-start mb-1">
                                                    <small className="fw-bold text-dark">Chest Pressure</small>
                                                    <small className="fw-bold text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>2d ago</small>
                                                </div>
                                                <span className="badge bg-success bg-opacity-10 text-success rounded-pill fw-bold" style={{ fontSize: '0.6rem' }}>RESOLVED</span>
                                            </div>
                                            <div className="p-3 border rounded-3 opacity-50">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <small className="fw-bold text-dark">Dry Cough</small>
                                                    <small className="fw-bold text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Sep 28</small>
                                                </div>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>

                                {/* PROMO */}
                                <Card className="border-0 shadow-sm rounded-4 text-white position-relative overflow-hidden" style={{ backgroundColor: primaryColor }}>
                                    <Card.Body className="p-4 position-relative z-1">
                                        <h5 className="fw-bold mb-2 small text-white">Predict Your Health</h5>
                                        <p className="small text-white-50 mb-4 lh-sm">
                                            Answer a few simple questions and let our machine learning model analyze potential diseases.
                                        </p>
                                        <Button variant="white" className="w-100 fw-bold text-dark small py-2 rounded-3 bg-white border-0 mt-4" onClick={predictHealth}>
                                            Start Health Questionnaire
                                        </Button>
                                    </Card.Body>
                                    <Icon
                                        icon="solar:clipboard-check-bold"
                                        className="position-absolute top-0 end-0 text-white opacity-10"
                                        style={{ fontSize: '3rem', margin: '0.5rem 0.5rem 0 0' }}
                                    />
                                </Card>

                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
            <HealthCheck show={hc} onHide={()=>setHc(false)} system={DiseasePredictionSystem}/>
        </div>
    );
};

export default EnterpriseSymptomChecker;